import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import GoalButton from "../components/GoalButton";
import ErrorAlert from "components/ErrorAlert";
import Author from "layouts/dashboard/components/TableAuthor";
import useGoals from "../useGoals";
import useGoalTemplates from "../useGoalTemplates";
import GoalSetupForm from "../components/GoalSetupForm";
import GoalStatusChip from "../components/GoalStatusChip";
import GoalPeriodCard from "../components/GoalPeriodCard";

function EngineerGoals() {
  const {
    auth: { admin, team, channel },
  } = useSelector((state) => state);
  const { userChannelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const goalsHook = useGoals({ adminId: admin?.id, teamId: team?.id, channelId: channel?.id });
  const templatesHook = useGoalTemplates(admin?.id);
  const {
    engineerGoals,
    isEngineerGoalsLoading,
    fetchEngineerGoals,
    errorMessage,
    successMessage,
    clearError,
    clearSuccess,
    createUserGoal,
    updateUserGoal,
    confirmSetup,
  } = goalsHook;

  // Setup happens inline on this page (not a modal): `isSettingUp` swaps the
  // form in above the goal tables, and `editTarget` is the period being edited
  // (null = creating a brand new one).
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  useEffect(() => {
    fetchEngineerGoals(userChannelId);
  }, [admin?.id, userChannelId]);

  useEffect(() => {
    if (admin?.id) templatesHook.fetchTemplates();
  }, [admin?.id]);

  const engineer = engineerGoals?.engineer;
  const periods = engineerGoals?.periods || [];
  // Periods come back newest first. Only one period can be unfinished at a
  // time, so that (if any) is the engineer's current one.
  const currentPeriod = periods.find((period) => period.status !== "completed");
  const currentStatus = currentPeriod?.status;

  const startSetup = () => {
    setEditTarget(currentPeriod || null);
    setIsSettingUp(true);
  };

  const finishSetup = () => {
    setIsSettingUp(false);
    setEditTarget(null);
    fetchEngineerGoals(userChannelId);
  };

  // The Goals list's "Set Goals" / "Edit Setup" buttons land here already
  // wanting the form open. Consume the flag so a refresh doesn't reopen it.
  useEffect(() => {
    if (engineerGoals && location.state?.startSetup) {
      startSetup();
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [engineerGoals]);

  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle={engineer?.display_name || engineer?.name || ""} />
      {errorMessage && <ErrorAlert color="error" message={errorMessage} clearError={clearError} />}
      {successMessage && (
        <ErrorAlert color="success" message={successMessage} clearError={clearSuccess} />
      )}

      <SuiBox mb={3}>
        <GoalButton
          variant="text"
          size="small"
          icon="arrow_back"
          onClick={() => navigate("/goals")}
        >
          Back to Goals
        </GoalButton>
      </SuiBox>

      {isEngineerGoalsLoading && !engineerGoals ? (
        <SuiBox display="flex" justifyContent="center" py={6}>
          <CircularProgress color="dark" size={30} />
        </SuiBox>
      ) : (
        engineer && (
          <>
            <Card sx={{ mb: 3 }}>
              <SuiBox p={3}>
                <SuiBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <SuiBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <Author
                      image={engineer.image_url}
                      name={engineer.display_name || engineer.name}
                      jobTitle={engineer.job_title}
                    />
                    <GoalStatusChip status={currentStatus} />
                  </SuiBox>
                  {!isSettingUp && (
                    <GoalButton
                      variant="primary"
                      icon={currentPeriod ? "edit" : "flag"}
                      onClick={startSetup}
                    >
                      {currentPeriod ? "Edit Setup" : "Set Goals"}
                    </GoalButton>
                  )}
                </SuiBox>
              </SuiBox>
            </Card>

            {isSettingUp && (
              <GoalSetupForm
                engineer={engineer}
                existingGoal={editTarget}
                templates={templatesHook.templates}
                onSubmit={createUserGoal}
                onUpdate={updateUserGoal}
                onConfirmSetup={confirmSetup}
                onCancel={finishSetup}
                onDone={finishSetup}
              />
            )}

            {isEmpty(periods) && !isSettingUp && (
              <Card>
                <SuiBox p={3} display="flex" flexDirection="column" alignItems="center">
                  <Icon fontSize="large" color="secondary" sx={{ opacity: 0.5 }}>
                    flag
                  </Icon>
                  <SuiTypography variant="button" color="text" fontWeight="regular" mt={1}>
                    No goals have been set for this engineer yet.
                  </SuiTypography>
                </SuiBox>
              </Card>
            )}

            {periods
              // The period being edited is shown as the form, not as a card too.
              .filter((period) => !(isSettingUp && period.id === editTarget?.id))
              .map((period) => (
                <GoalPeriodCard
                  key={period.id}
                  period={period}
                  isCurrent={period.id === currentPeriod?.id}
                  hook={goalsHook}
                  onChanged={() => fetchEngineerGoals(userChannelId)}
                />
              ))}
          </>
        )
      )}

      <SuiBox mt={3}>
        <Footer />
      </SuiBox>
    </DashboardLayout>
  );
}

export default EngineerGoals;
